import pako from "pako";

/** ETHUSDT / ETH_USDT / ETH-USDT  →  ETH-USDT */
export function fmtBingxSymbol(sym) {
  if (!sym) return "";
  const s = sym.replace(/_/g, "-");
  if (/-/.test(s)) return s.toUpperCase();
  const m = s.match(/^([A-Z0-9]+)(USDT|USD|USDC|BTC|ETH)$/i);
  if (m) return `${m[1]}-${m[2]}`.toUpperCase();
  return s.toUpperCase();
}

/** маппинг таймфреймов приложения → BingX */
export function mapIntervalToBingx(tf) {
  const map = {
    "1m": "1m",  "3m": "3m",  "5m": "5m", "15m": "15m", "30m": "30m",
    "1h": "1h",  "2h": "2h",  "4h": "4h", "6h": "6h",   "8h": "8h",
    "12h":"12h", "1d": "1d",  "3d": "3d", "1w": "1w",
  };
  return map[tf] || "1m";
}

/** lightweight-charts формат */
export const toLwc = (t, o, h, l, c) => ({ time: t, open: o, high: h, low: l, close: c });

/** REST массив → свеча */
export function normalizeCandleFromArray(row) {
  // [openTime, open, high, low, close, volume, closeTime]
  const t = Number(row?.[0]);
  const o = Number(row?.[1]);
  const h = Number(row?.[2]);
  const l = Number(row?.[3]);
  const c = Number(row?.[4]);
  if (![t, o, h, l, c].every(Number.isFinite)) return null;
  const tSec = t > 2e10 ? Math.floor(t / 1000) : (t >= 1e9 ? t : Math.floor(t));
  return toLwc(tSec, o, h, l, c);
}

/** REST/WS объект → свеча (гибкий парсер) */
export function normalizeCandleFromObject(obj) {
  if (!obj || typeof obj !== "object") return null;

  let tRaw = obj.t ?? obj.T ?? obj.time ?? obj.openTime ?? obj.startTime ?? obj.ts ?? obj.timestamp;
  let tMs = null;

  if (typeof tRaw === "number") {
    tMs = tRaw > 2e10 ? tRaw : (tRaw < 1e12 ? (tRaw * 1000) : tRaw);
  } else if (typeof tRaw === "string") {
    if (/^\d+$/.test(tRaw)) {
      const n = Number(tRaw);
      tMs = n > 2e10 ? n : (n < 1e12 ? (n * 1000) : n);
    } else {
      const p = Date.parse(tRaw);
      if (Number.isFinite(p)) tMs = p;
    }
  }
  if (!Number.isFinite(tMs)) return null;

  const o = toNum(obj.o) ?? toNum(obj.open)  ?? toNum(obj.op);
  const h = toNum(obj.h) ?? toNum(obj.high);
  const l = toNum(obj.l) ?? toNum(obj.low);
  const c = toNum(obj.c) ?? toNum(obj.close);
  if (![o, h, l, c].every(Number.isFinite)) return null;

  return toLwc(Math.floor(tMs / 1000), o, h, l, c);
}

function toNum(v) { const n = Number(v); return Number.isFinite(n) ? n : null; }

/** Универсальная нормализация: массив или объект */
export function normalizeCandleFlexible(row) {
  if (Array.isArray(row)) return normalizeCandleFromArray(row);
  return normalizeCandleFromObject(row);
}

/** Gunzip + safe JSON */
export async function decodeWsMessage(event) {
  try {
    if (event.data instanceof ArrayBuffer) {
      const text = pako.ungzip(new Uint8Array(event.data), { to: "string" });
      return safeJson(text);
    }
    if (event.data instanceof Blob) {
      const ab = await event.data.arrayBuffer();
      const text = pako.ungzip(new Uint8Array(ab), { to: "string" });
      return safeJson(text);
    }
    if (typeof event.data === "string") {
      try {
        const u8 = Uint8Array.from(event.data, (c)=>c.charCodeAt(0));
        const text = pako.ungzip(u8, { to: "string" });
        return safeJson(text);
      } catch { return safeJson(event.data); }
    }
  } catch {}
  return null;
}

function safeJson(s) { try { return JSON.parse(s); } catch { return s; } }

/**
 * Универсальный экстрактор свечей из WS-пейлоадов BingX:
 * возвращает { candle, candles, closed }
 */
export function extractWsKline(msg) {
  const d = msg?.data ?? msg?.kline ?? msg?.k ?? msg?.result ?? msg;
  if (!d) return null;

  if (typeof d === "object" && !Array.isArray(d)) {
    const t = Number(d.t ?? d.startTime ?? d.openTime ?? d.time);
    const o = Number(d.o ?? d.open);
    const h = Number(d.h ?? d.high);
    const l = Number(d.l ?? d.low);
    const c = Number(d.c ?? d.close);
    const isClosed = Boolean(d.x ?? d.isClosed);
    if ([t,o,h,l,c].every(Number.isFinite)) {
      const tSec = t > 2e10 ? Math.floor(t/1000) : (t >= 1e9 ? t : Math.floor(t));
      return { candle: toLwc(tSec, o,h,l,c), closed: isClosed };
    }
    const one = normalizeCandleFromObject(d);
    if (one) return { candle: one, closed: isClosed };
  }

  if (Array.isArray(d)) {
    if (d.length && typeof d[0] === "object" && !Array.isArray(d[0])) {
      const candles = d.map(normalizeCandleFromObject).filter(Boolean);
      const last = candles[candles.length - 1];
      const lastRaw = d[d.length - 1];
      const isClosed = Boolean(lastRaw?.x ?? lastRaw?.isClosed ?? true);
      if (last) return { candle: last, candles, closed: isClosed };
      return null;
    }
    const candles = d.map(normalizeCandleFromArray).filter(Boolean);
    const last = candles[candles.length - 1];
    if (last) return { candle: last, candles, closed: true };
  }

  return null;
}

export function randomId() {
  try {
    const b = new Uint8Array(8);
    crypto.getRandomValues(b);
    return Array.from(b).map(x=>x.toString(16).padStart(2,"0")).join("");
  } catch { return String(Date.now()); }
}
